import { DateTime } from 'luxon';
import { StatusMode } from 'src/app/store/auth/auth.actions';
import { Worklog } from 'src/models';
import { GetWorkSchedulesParams, LeaveType, Shift } from '../schedules';
import { DEMO_TIMELINE_PROJECTS } from './mock-timeline-worklogs';
import { MockDataStore, User as MockUser } from './mock-data-store';
import { DemoSeedService } from './demo-seed.service';

const ALL_USERS_FILTERS = new Set(['all-on-reports', 'all-self-on-reports']);
const DEMO_DEVICE_ID = 'demo-attendance-device';
const SHIFT_HOURS = 8;
const SHIFT_START_HOUR = 9;

type AttendanceDayVariant = 'present' | 'late' | 'partial' | 'absent' | 'paidLeave';

function isAllUsersFilter(user: string | string[] | undefined): boolean {
  return typeof user === 'string' && ALL_USERS_FILTERS.has(user);
}

function filterUsers(
  options: { user?: string | string[] },
  users: MockUser[],
): MockUser[] {
  const filtered = users.filter(u => !u.archived);

  if (!options.user || isAllUsersFilter(options.user)) {
    return filtered;
  }

  const ids = Array.isArray(options.user)
    ? options.user.map(String)
    : String(options.user).split(',').filter(Boolean);

  if (ids.length === 0) {
    return [];
  }

  return filtered.filter(u => ids.includes(u.id));
}

function resolveTimezone(options: { timezone?: string }): string {
  return options.timezone || 'UTC';
}

/** Inclusive local calendar-day bounds for API `from` / exclusive `to` instants. */
function resolveDateRange(options: { from?: string, to?: string, timezone?: string }) {
  const zone = resolveTimezone(options);
  const fallbackFrom = DateTime.now().setZone(zone).minus({ days: 7 }).startOf('day');
  const fallbackToExclusive = DateTime.now().setZone(zone).plus({ days: 1 }).startOf('day');
  const fromDay = DateTime.fromISO(options.from || fallbackFrom.toUTC().toISO(), { zone }).startOf('day');
  const toExclusive = DateTime.fromISO(options.to || fallbackToExclusive.toUTC().toISO(), { zone });
  const lastDay = toExclusive.minus({ millisecond: 1 }).startOf('day');
  return { fromDay, lastDay, zone };
}

function userVariant(userId: string, dayIndex: number, seed: DemoSeedService): AttendanceDayVariant {
  const roll = seed.int(0, 99, 'attendance-variant', userId, dayIndex);
  const userNum = Number(/(\d+)$/.exec(userId)?.[1] || 1);

  if (userNum === 4 || roll < 8) {
    return 'absent';
  }
  if (userNum === 5 && roll < 20) {
    return 'paidLeave';
  }
  if (userNum === 2 || (roll >= 20 && roll < 35)) {
    return 'late';
  }
  if (userNum === 3 || (roll >= 35 && roll < 50)) {
    return 'partial';
  }
  return 'present';
}

function eachWeekday(
  fromDay: DateTime,
  lastDay: DateTime,
  fn: (day: DateTime, index: number) => void,
): void {
  let day = fromDay.startOf('day');
  const end = lastDay.startOf('day');
  let index = 0;
  while (day <= end) {
    if (day.weekday <= 5) {
      fn(day, index);
      index += 1;
    }
    day = day.plus({ days: 1 });
  }
}

function buildShiftForDay(
  userId: string,
  day: DateTime,
  variant: AttendanceDayVariant,
): Shift {
  const start = day.set({ hour: SHIFT_START_HOUR, minute: 0, second: 0, millisecond: 0 });
  const end = start.plus({ hours: SHIFT_HOURS });
  const leaveType: LeaveType | undefined = variant === 'paidLeave' ? 'paidLeave' : undefined;

  return {
    id: `demo-shift-${userId}-${day.toISODate()}`,
    userId,
    start: start.toUTC().toISO(),
    end: end.toUTC().toISO(),
    minimumHours: SHIFT_HOURS,
    ...(leaveType ? { leaveType, approved: 'approved' as const } : {}),
  };
}

function buildWorklogsForShift(
  seed: DemoSeedService,
  shift: Shift,
  variant: AttendanceDayVariant,
): Worklog[] {
  if (variant === 'absent' || variant === 'paidLeave' || !shift.start || !shift.end) {
    return [];
  }

  const shiftStart = DateTime.fromISO(shift.start);
  const project = DEMO_TIMELINE_PROJECTS[
    seed.pickIndex(DEMO_TIMELINE_PROJECTS.length, 'attendance-project', shift.userId, shift.start)
  ];

  let startOffsetMin = 0;
  let workedSeconds = SHIFT_HOURS * 3600;

  if (variant === 'late') {
    startOffsetMin = 15 + seed.int(0, 25, 'attendance-late', shift.userId, shift.start);
    workedSeconds = SHIFT_HOURS * 3600;
  } else if (variant === 'partial') {
    startOffsetMin = seed.int(0, 10, 'attendance-partial-start', shift.userId, shift.start);
    workedSeconds = (3 + seed.int(0, 2, 'attendance-partial-hours', shift.userId, shift.start)) * 3600;
  } else {
    startOffsetMin = seed.int(0, 4, 'attendance-present', shift.userId, shift.start);
  }

  const wlStart = shiftStart.plus({ minutes: startOffsetMin });
  const mode: StatusMode = 'computer';

  return [{
    deviceId: DEMO_DEVICE_ID,
    mode,
    projectId: project.id,
    projectName: project.name,
    start: wlStart.toUTC().toISO(),
    taskId: `demo-task-${shift.userId}`,
    taskName: 'Scheduled work',
    time: workedSeconds,
    userId: shift.userId,
  }];
}

/** Mock work-schedule shifts for the Attendance report sample-data path. */
export function buildMockAttendanceShifts(
  seed: DemoSeedService,
  params: GetWorkSchedulesParams,
): Shift[] {
  const users = filterUsers(params, MockDataStore.getInstance().getAllUsers());
  const { fromDay, lastDay } = resolveDateRange(params);
  const shifts: Shift[] = [];

  users.forEach(user => {
    eachWeekday(fromDay, lastDay, (day, dayIndex) => {
      // Include future weekdays so Schedules (planning) can show a full week of sample
      // shifts. Attendance filters future days client-side after fetch.
      const variant = userVariant(user.id, dayIndex, seed);
      shifts.push(buildShiftForDay(user.id, day, variant));
    });
  });

  return shifts;
}

/** Mock worklogs aligned to attendance shifts (present / late / partial / absent / leave). */
export function buildMockAttendanceWorklogs(
  seed: DemoSeedService,
  fromIso: string,
  toIso: string,
  userIds: string[],
  timezone = 'UTC',
): Worklog[] {
  const users = MockDataStore.getInstance().getAllUsers()
    .filter(u => !u.archived && (!userIds?.length || userIds.includes(u.id)));
  const { fromDay, lastDay, zone } = resolveDateRange({ from: fromIso, to: toIso, timezone });
  const today = DateTime.now().setZone(zone).startOf('day');
  const worklogs: Worklog[] = [];

  users.forEach(user => {
    eachWeekday(fromDay, lastDay, (day, dayIndex) => {
      if (day.startOf('day') > today) {
        return;
      }
      const variant = userVariant(user.id, dayIndex, seed);
      const shift = buildShiftForDay(user.id, day, variant);
      worklogs.push(...buildWorklogsForShift(seed, shift, variant));
    });
  });

  return worklogs;
}
